import { useScrollReveal } from "@/hooks/useScrollReveal";
import amenitiesSunriseImg from "@/assets/amenities-sunrise.png";
import cactusImg from "@/assets/cactus.png";
import sheepImg from "@/assets/sheep.png";
import farmhouseImg from "@/assets/farmhouse.png";



const facilities = [
  { icon: "ri-restaurant-line", title: "\u00a0Kitchen", desc: "Fully equipped — everything you need to cook a meal fit for a king." },
  { icon: "ri-fire-line", title: "Braai Area", desc: "An authentic South African braai setup for long evenings under the stars. *Guests are required to provide their own firewood" },
  { icon: "ri-temp-cold-line", title: "Fireplace", desc: "A cozy fireplace for crisp Free State evenings — warm, quiet, and unhurried. *Guests are required to provide their own firewood" },
  { icon: "ri-car-line", title: "Private Parking", desc: "Safe, private parking right at the house." },
  { icon: "ri-heart-line", title: "Pet Friendly", desc: "Well-behaved pets are welcome — plenty of open space to roam." },
  { icon: "ri-group-line", title: "Family Friendly", desc: "All ages welcome. Tranquility and freedom for the whole family." },
];

const activities = [
  { icon: "ri-walk-line", title: "Hiking & Walking", desc: "Discover the beauty of the open farmland on foot." },
  { icon: "ri-bike-line", title: "Cycling", desc: "Enjoy cycling along the farm's gravel paths." },
  { icon: "ri-water-flash-line", title: "Fishing & Swimming", desc: "Enjoy the farm's natural water features — peaceful, unhurried, and restorative. *seasonal" },
  { icon: "ri-eye-line", title: "Bird Watching", desc: "The farm's pans attract a rich variety of bird life throughout the year." },
  { icon: "ri-moon-line", title: "Stargazing", desc: "No city lights. Just you and one of the most spectacular night skies in South Africa." },
  { icon: "ri-leaf-line", title: "Farm Life", desc: "Experience the peace and simplicity of farm life, with complimentary opportunities to milk cows and feed orphaned calves available by prior arrangement." },
];

export default function Amenities() {
  const ref = useScrollReveal();

  return (
    <section id="amenities" ref={ref} className="overflow-hidden" style={{ backgroundColor: "#F0EBE0" }}>

      {/* Full-width cinematic pause */}
      <div className="img-reveal w-full relative overflow-hidden grain" style={{ height: "72vh" }}>
        <img
          alt="Sunrise over Rietkuil farm fields"
          className="w-full h-full object-cover object-center img-washed"
          src={amenitiesSunriseImg}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(30,22,8,0.55) 0%, rgba(30,22,8,0.1) 60%, transparent 100%)" }}></div>
        <div className="absolute inset-0 flex items-end px-10 md:px-20 pb-16 md:pb-20">
          <div>
            <p className="fade-in text-base tracking-[0.1em] uppercase font-sans font-medium mb-4" style={{ color: "rgba(255,255,255,0.9)" }}>Facilities &amp; Activities</p>
            <h2 className="fade-in delay-200 font-serif font-medium text-white leading-none" style={{ fontSize: "clamp(3rem, 6vw, 5.5rem)", textShadow: "0 1px 2px rgba(0,0,0,0.25), 0 0 40px rgba(0,0,0,0.15)" }}>
              Everything<br />you need
            </h2>
          </div>
        </div>
      </div>

      {/* Facilities — no boxes, just air */}
      <div className="max-w-5xl mx-auto px-8 md:px-16 pt-24 pb-16">
        <p className="fade-in text-base tracking-[0.1em] uppercase font-sans font-medium mb-14 text-center" style={{ color: "#4E4228" }}>Facilities</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-14">
          {facilities.map((a, i) => (
            <div key={a.title} className={`fade-in delay-${Math.min(i * 100, 500)}`}>
              <div className="w-7 h-7 flex items-center justify-center mb-5">
                <i className={`${a.icon} text-base`} style={{ color: "#87917C" }}></i>
              </div>
              <h3 className="text-sm font-serif font-light mb-2 tracking-wide" style={{ color: "#4E4228" }}>{a.title}</h3>
              <p className="text-xs font-sans font-light leading-relaxed" style={{ color: "#A8A090" }}
                dangerouslySetInnerHTML={{ __html: a.desc }}></p>
            </div>
          ))}
        </div>
      </div>

      {/* Activities */}
      <div className="max-w-5xl mx-auto px-8 md:px-16 pb-20">
        <div className="w-full h-px mb-16" style={{ backgroundColor: "#C8C0B4" }}></div>
        <p className="fade-in text-base tracking-[0.1em] uppercase font-sans font-medium mb-14 text-center" style={{ color: "#4E4228" }}>Activities &amp; Experiences</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-10 gap-y-14">
          {activities.map((a, i) => (
            <div key={a.title} className={`fade-in delay-${Math.min(i * 100, 500)}`}>
              <div className="w-7 h-7 flex items-center justify-center mb-5">
                <i className={`${a.icon} text-base`} style={{ color: "#87917C" }}></i>
              </div>
              <h3 className="text-sm font-serif font-light mb-2 tracking-wide" style={{ color: "#4E4228" }}>{a.title}</h3>
              <p className="text-xs font-sans font-light leading-relaxed" style={{ color: "#A8A090" }}
                dangerouslySetInnerHTML={{ __html: a.desc }}></p>
            </div>
          ))}
        </div>
      </div>

      {/* Farm photo trio — house in the middle */}
      <div className="max-w-7xl mx-auto px-8 md:px-16 pb-28">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="img-reveal overflow-hidden" style={{ height: "520px" }}>
            <img alt="Flowering cacti on the farm" className="w-full h-full object-cover object-center img-washed" src={cactusImg} />
          </div>
          <div className="img-reveal delay-100 overflow-hidden" style={{ height: "520px" }}>
            <img alt="The Rietkuil farmhouse" className="w-full h-full object-cover object-center img-washed" src={farmhouseImg} />
          </div>
          <div className="img-reveal delay-200 overflow-hidden" style={{ height: "520px" }}>
            <img alt="Sheep at Rietkuil farm" className="w-full h-full object-cover object-center img-washed" src={sheepImg} />
          </div>
        </div>
      </div>

    </section>
  );
}
